import { useQuery } from '@tanstack/react-query';
import { fetchPracticeRiskStatuses, type PracticeRiskEntry } from '../services/practiceRisk';
import { PRACTICE_RISK_STATUSES_KEY } from './queryKeys';

const STALE_TIME_MS = 5 * 60_000;

export interface PracticeRiskLookup {
    byId: Map<string, PracticeRiskEntry>;
    byName: Map<string, PracticeRiskEntry>;
}

const EMPTY_LOOKUP: PracticeRiskLookup = { byId: new Map(), byName: new Map() };

// byName keys on a trimmed/lowercased name - most rows carry only a practice
// NAME, not its id (see resolvePracticeRisk). Names aren't unique, so a
// collision removes that key from byName (ambiguousNames blocks a re-add) -
// an ambiguous name gets no coloring, never another practice's.
function buildLookup(entries: PracticeRiskEntry[] | undefined): PracticeRiskLookup {
    if (!entries || entries.length === 0) return EMPTY_LOOKUP;
    const byId = new Map<string, PracticeRiskEntry>();
    const byName = new Map<string, PracticeRiskEntry>();
    const ambiguousNames = new Set<string>();
    for (const entry of entries) {
        byId.set(entry.id, entry);
        const nameKey = entry.name.trim().toLowerCase();
        if (!nameKey) continue;
        if (ambiguousNames.has(nameKey)) continue;
        if (byName.has(nameKey)) {
            byName.delete(nameKey);
            ambiguousNames.add(nameKey);
            continue;
        }
        byName.set(nameKey, entry);
    }
    return { byId, byName };
}

// Keyed on the query's data array so every hook instance shares one built
// lookup instead of rebuilding identical Maps per row.
const lookupCache = new WeakMap<PracticeRiskEntry[], PracticeRiskLookup>();

function getOrBuildLookup(entries: PracticeRiskEntry[] | undefined): PracticeRiskLookup {
    if (!entries) return EMPTY_LOOKUP;
    const cached = lookupCache.get(entries);
    if (cached) return cached;
    const lookup = buildLookup(entries);
    lookupCache.set(entries, lookup);
    return lookup;
}

/**
 * Global lookup of every practice currently flagged warning/at_risk (absent =
 * normal - see services/practiceRisk.ts). Backs the risk coloring/tooltip
 * everywhere a patient/practice sublabel is rendered (see
 * components/shared/PracticeDoctorLabel.tsx and PatientPracticeCell.tsx) - one
 * shared query rather than a per-row fetch, since the flagged list is small
 * and changes rarely.
 */
export function usePracticeRiskStatuses() {
    const query = useQuery({
        queryKey: PRACTICE_RISK_STATUSES_KEY,
        queryFn: fetchPracticeRiskStatuses,
        staleTime: STALE_TIME_MS
    });

    const lookup = getOrBuildLookup(query.data);

    return { lookup, isLoading: query.isLoading, isError: query.isError };
}

/**
 * Resolves a row's risk entry. When a practiceId is supplied it is
 * authoritative - a miss in byId means normal, and the name is never
 * consulted. The name lookup only runs when no id was provided at all.
 */
export function resolvePracticeRisk(
    lookup: PracticeRiskLookup,
    ref: { practiceId?: string | null; practiceName?: string | null }
): PracticeRiskEntry | undefined {
    if (ref.practiceId) {
        return lookup.byId.get(ref.practiceId);
    }
    const nameKey = ref.practiceName?.trim().toLowerCase();
    if (!nameKey) return undefined;
    return lookup.byName.get(nameKey);
}
